/**
 * Reduce a "send me back here afterwards" value to something safe to obey.
 *
 * A sign-in link that carries a destination is a phishing primitive if the
 * destination can be anywhere: the mail arrives from the venue's own domain,
 * looks exactly like every other mail from it, and lands the reader on a page
 * somebody else wrote. The defense is that only an in-app path is ever honored,
 * decided here rather than at each call site.
 *
 * The traps are all about what a browser treats as "another host":
 *
 * - `//evil.example` and `/\evil.example` are **protocol-relative** — a leading
 *   `/` is not enough to keep you on this origin, and browsers normalize the
 *   backslash form to the slash form.
 * - `https://evil.example` is obvious; `javascript:…` less so, and neither
 *   starts with `/`.
 * - A newline is refused on principle: nothing downstream of this should be
 *   putting the value in a header, but a value that could not survive being put
 *   in one is cheaper to reject than to audit.
 *
 * Returns null for anything it is not certain of, and the caller falls back to
 * wherever that person normally lands. Being sent to your own home page is a
 * small disappointment; being sent to somebody else's is the whole problem.
 */

/** Long enough for any real route with a query; short enough to bound the column. */
const MAX_LENGTH = 512

export function safeRedirectPath(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > MAX_LENGTH) return null

  // One leading slash, and exactly one: the two-character forms below are read
  // by browsers as "//host", which leaves this origin entirely.
  if (!trimmed.startsWith('/')) return null
  if (trimmed.startsWith('//') || trimmed.startsWith('/\\')) return null

  // Control characters, including the newline a header-injection attempt would
  // need. Checked by code point rather than by a regex literal, which would have
  // had to contain the very bytes it is looking for.
  for (const character of trimmed) {
    const code = character.codePointAt(0) ?? 0
    if (code < 0x20 || code === 0x7f) return null
  }

  return trimmed
}
